import React, { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { IProps } from './prospect-container';
import Suspense from '../../shared/components/suspense';
import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import retryLazy from '../retry-lazy';
import ProspectHeader from './components/prospect-header/prospect-header-container';
import ProspectFiltersModal from './components/prospect-filters-modal';
import { useProspectDetailsUrl } from '../../shared/hooks/use-prospect-details-url';
import {
  doesFieldExistInConditions,
  executeWithValueCheck,
} from './helpers/helper';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
} from '../../shared/utils';
import toaster from '../../shared/toaster';
import { BulkActionProspect, ViewType } from './enums/prospect';
import AddToSequenceReportModal from '../../shared/design-system/components/atoms/report-modal';
import { reportConstants } from '../../shared/design-system/components/atoms/report-page/constants/report-constants';
import Icon from '../../shared/design-system/components/atoms/icon';
import ImageIcon from '../../shared/components/images/image-icon';
import { Images } from '../../shared/app-constants';

const ProspectList = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "prospect-list" */ './components/prospect-list'
      ),
  ),
);

const ProspectDetails = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "prospect-details" */ './components/prospect-list/components/prospect-details/prospect-details-container'
      ),
  ),
);

const ProspectListContent = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "prospect-list-content" */ './components/prospect-list-content'
      ),
  ),
);

const ProspectKanban = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "prospect-kanban" */ './components/prospect-kanban'
      ),
  ),
);

const Prospect: React.FC<IProps> = ({
  sendGetFieldsRequest,
  setProspectDetails,
  sendGetSingleProspectDetailsRequest,
  singleProspectData,
  singleProspectSequenceDetails,
  getSingleProspectDetailsStatus,
  getSingleProspectSequencesDetailsStatus,
  resetProspectFiltersFieldsAttributesValue,
  setActiveViewIdValue,
  setCurrentViewTypeValue,
  setProspectToNavigateFromModal,
  setShowProspectDetailModal,
  addToStepRequestReportData,
  addToStepRequestSuccessfulCount,
  addToStepRequestFailedCount,
  unsubscribeProspectStatus,
  unsubscribeProspectError,
  unsubscribeProspectMessage,
  sendUnsubscribeProspectRequest,
  sendUnsubscribeBulkProspectsRequestStatus,
  sendUnsubscribeBulkProspectsRequestError,
  sendUnsubscribeBulkProspectsRequestMessage,
  filters,
  resetBulkRequests,
  resetUnsubscribeRequestState,
  updateProspectFiltersViewRequestStatus,
  ...prospectDetailsProps
}) => {
  const history = useHistory();
  const location = useLocation();
  const { prospectId: prospectIdFromParam, tab: tabParam } = useParams<{
    prospectId?: string;
    tab?:
      | 'activity'
      | 'sequences'
      | 'contacts'
      | 'notes'
      | 'tasks'
      | 'email'
      | 'calls';
  }>();
  const { openProspectDetails, closeProspectDetails } = useProspectDetailsUrl();

  const [showProspectDetailsModal, setShowProspectDetailsModal] = useState(
    false,
  );
  const [selectedContactId, setSelectedContactId] = useState<number>(null);
  const [showProspectFilterModal, setShowProspectFilterModal] = useState(false);

  const handleCloseProspectFilterModal = useCallback(() => {
    setShowProspectFilterModal(false);
  }, []);
  const [showAddToSequenceReportModal, setAddToSequenceReportModal] = useState(
    false,
  );

  useEffect(() => {
    sendGetFieldsRequest();
  }, [sendGetFieldsRequest]);

  useEffect(() => {
    setShowProspectDetailModal(showProspectDetailsModal);
  }, [showProspectDetailsModal]);

  const {
    addToStepRequestStatus,
    addToStepRequestError,
  } = prospectDetailsProps;

  const reportDataStructure = {
    heading: reportConstants.ADD_PROSPECTS_TO_SEQUENCE_REPORT,
    sections: [
      {
        topic: reportConstants.SUCCESSFULLY_MOVED,
        value: addToStepRequestSuccessfulCount,
        icon: <Icon identifier="check-o" className="lime-txt-7" />,
      },
      {
        topic: reportConstants.FAILED_TO_MOVE,
        value: addToStepRequestFailedCount,
        icon: <Icon identifier="danger" className="gold-txt-6" />,
      },
    ],
    reportData: {
      failedResultData: addToStepRequestReportData,
    },
  };

  // Handle prospect page URL Values
  useEffect(() => {
    // Handle View Id
    const urlParams = new URLSearchParams(location.search || '');
    const viewId = Number(urlParams.get('viewId')) || null;
    setActiveViewIdValue(viewId);

    // Handle Prospect Id
    const prospectId = Number(prospectIdFromParam) || null;
    setSelectedContactId(prospectId);
    setProspectDetails(prospectId);
    setShowProspectDetailsModal(Boolean(prospectId));
  }, [
    prospectIdFromParam,
    tabParam,
    location.search,
    setProspectDetails,
    setActiveViewIdValue,
  ]);

  // Set current view type based on route
  useEffect(() => {
    const isKanbanRoute = location.pathname.includes('/crm/kanban');
    const viewType = isKanbanRoute ? ViewType.Kanban : ViewType.List;
    setCurrentViewTypeValue(viewType);
  }, [location.pathname, setCurrentViewTypeValue]);

  // Listen for events to open/close prospect details modal
  useEffect(() => {
    const handleOpenProspectDetails = (event: CustomEvent) => {
      const { prospectId, tab } = event.detail || {};
      if (prospectId) {
        const idNum = Number(prospectId);
        if (!Number.isNaN(idNum)) {
          setSelectedContactId(idNum);
          setProspectDetails(idNum);
          setShowProspectDetailsModal(true);

          // Update URL with prospectId and tab, preserving query parameters
          openProspectDetails(idNum, tab as any);
        }
      }
    };

    const handleCloseProspectDetails = () => {
      setShowProspectDetailsModal(false);
      setSelectedContactId(null);
      setProspectToNavigateFromModal({ ids: [], columnName: '' });

      // Reset URL to base route, preserving query parameters
      closeProspectDetails();
    };

    window.addEventListener(
      'prospect:openProspectDetailsModal',
      handleOpenProspectDetails as EventListener,
    );
    window.addEventListener(
      'prospect:closeProspectDetailsModal',
      handleCloseProspectDetails,
    );

    return () => {
      window.removeEventListener(
        'prospect:openProspectDetailsModal',
        handleOpenProspectDetails as EventListener,
      );
      window.removeEventListener(
        'prospect:closeProspectDetailsModal',
        handleCloseProspectDetails,
      );
    };
  }, [openProspectDetails, closeProspectDetails, setProspectDetails]);

  useEffect(() => {
    const handleOpenAddToSequenceReportModal = () => {
      setAddToSequenceReportModal(true);
    };

    const handleCloseAddToSequenceReportModal = () => {
      setAddToSequenceReportModal(false);
    };

    window.addEventListener(
      'prospect:openAddToSequenceReportModal',
      handleOpenAddToSequenceReportModal,
    );

    window.addEventListener(
      'prospect:hideAddToSequenceReportModal',
      handleCloseAddToSequenceReportModal,
    );

    return () => {
      window.removeEventListener(
        'prospect:openAddToSequenceReportModal',
        handleOpenAddToSequenceReportModal,
      );
      window.removeEventListener(
        'prospect:hideAddToSequenceReportModal',
        handleCloseAddToSequenceReportModal,
      );
    };
  }, []);

  const dispatchEvent = useCallback((name: string, detail?: any) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (e) {
      // ignore
    }
  }, []);

  const viewAddToSequenceModal = useCallback(
    () => dispatchEvent('prospect:openAddToSequenceModal'),
    [dispatchEvent],
  );
  const viewDeleteProspectModal = useCallback(
    () => dispatchEvent('prospect:openDeleteProspectModal'),
    [dispatchEvent],
  );
  const viewUnsubscribeProspectModal = useCallback(
    () => dispatchEvent('prospect:openUnsubscribeProspectModal'),
    [dispatchEvent],
  );
  const viewEmailVerificationModal = useCallback(
    () => dispatchEvent('prospect:openEmailVerificationModal'),
    [dispatchEvent],
  );
  const handleClearSelection = useCallback(
    () => dispatchEvent('prospect:clearSelection'),
    [dispatchEvent],
  );
  const fetchProspectListAndCount = useCallback(
    (options?: { doNoSendFilterGroupLogic?: boolean }) =>
      dispatchEvent('prospect:fetchProspectListAndCount', options),
    [dispatchEvent],
  );
  const viewFiltersModal = useCallback(
    () => dispatchEvent('prospect:openFiltersModal'),
    [dispatchEvent],
  );

  const hideAddToSequenceModal = useCallback(
    () => dispatchEvent('prospect:hideAddToSequenceModal'),
    [dispatchEvent],
  );

  const viewAddToSequenceReportModal = useCallback(
    () => dispatchEvent('prospect:openAddToSequenceReportModal'),
    [dispatchEvent],
  );

  const hideAddToSequenceReportModal = useCallback(
    () => dispatchEvent('prospect:hideAddToSequenceReportModal'),
    [dispatchEvent],
  );

  const resetSequenceAndStepData = useCallback(
    () => dispatchEvent('prospect:resetSequenceAndStepData'),
    [dispatchEvent],
  );

  const handleUpdateProspectList = useCallback(
    (options: {
      action: BulkActionProspect;
      fetchProspectList?: boolean;
      fetchProspectListCount?: boolean;
      resetPageNumber?: boolean;
    }) => dispatchEvent('prospect:updateProspectList', options),
    [dispatchEvent],
  );

  const hideUnsubscribeProspectModal = useCallback(
    () => dispatchEvent('prospect:hideUnsubscribeProspectModal'),
    [dispatchEvent],
  );

  // Listen for filter modal event
  useEffect(() => {
    const handleOpenFilters = () => {
      setShowProspectFilterModal(true);
    };

    window.addEventListener('prospect:openFiltersModal', handleOpenFilters);

    return () => {
      window.removeEventListener(
        'prospect:openFiltersModal',
        handleOpenFilters,
      );
    };
  }, []);

  // Reset filter field attributes when modal closes
  useEffect(() => {
    if (!showProspectFilterModal) {
      resetProspectFiltersFieldsAttributesValue({
        key: 'sequence',
        hardReset: true,
      });
      resetProspectFiltersFieldsAttributesValue({
        key: 'subSequence',
        hardReset: true,
      });
    }
  }, [showProspectFilterModal, resetProspectFiltersFieldsAttributesValue]);

  // * Add Step to Request
  useEffect(() => {
    executeWithValueCheck({
      value: !showProspectDetailsModal,
      handler: () => {
        executeOnRequestStatus({
          status: addToStepRequestStatus,
          onSuccess: () => {
            hideAddToSequenceModal();
            handleClearSelection();
            handleUpdateProspectList({
              action: BulkActionProspect.AddToStep,
              fetchProspectList: true,
              fetchProspectListCount: true,
              resetPageNumber: true,
            });
            resetSequenceAndStepData();
            viewAddToSequenceReportModal();
          },
          onFailed: () => {
            executeOnErrorWithErrorCheck({
              error: addToStepRequestError,
              onError: () => {
                handleClearSelection();
                resetSequenceAndStepData();
                toaster.error(addToStepRequestError.message);
              },
            });
          },
        });
      },
    });
  }, [addToStepRequestStatus]);

  useEffect(() => {
    executeWithValueCheck({
      value: !showProspectDetailsModal,
      handler: () => {
        executeOnRequestStatus({
          status: sendUnsubscribeBulkProspectsRequestStatus,
          onSuccess: () => {
            handleClearSelection();
            handleUpdateProspectList({
              action: BulkActionProspect.Unsubscribe,
              fetchProspectList: true,
              fetchProspectListCount: doesFieldExistInConditions(
                filters.filterGroupLogic,
                'status',
              ),
              resetPageNumber: false,
            });
            hideUnsubscribeProspectModal();
            resetSequenceAndStepData();
            toaster.success(sendUnsubscribeBulkProspectsRequestMessage, {
              delay: 3000,
            });
            resetBulkRequests();
          },
          onFailed: () => {
            executeOnErrorWithErrorCheck({
              error: sendUnsubscribeBulkProspectsRequestError,
              onError: () => {
                resetSequenceAndStepData();
                handleClearSelection();
                resetBulkRequests();
                toaster.error(
                  sendUnsubscribeBulkProspectsRequestError?.message,
                  {
                    delay: 5000,
                  },
                );
              },
            });
          },
        });
      },
    });
  }, [sendUnsubscribeBulkProspectsRequestStatus]);

  // * Unsubscribe Prospect Request
  useEffect(() => {
    executeWithValueCheck({
      value: !showProspectDetailsModal,
      handler: () => {
        executeOnRequestStatus({
          status: unsubscribeProspectStatus,
          onSuccess: () => {
            handleClearSelection();
            handleUpdateProspectList({
              action: BulkActionProspect.Unsubscribe,
              fetchProspectList: true,
              fetchProspectListCount: doesFieldExistInConditions(
                filters.filterGroupLogic,
                'status',
              ),
              resetPageNumber: false,
            });
            hideUnsubscribeProspectModal();
            resetSequenceAndStepData();
            toaster.success(unsubscribeProspectMessage);
            resetUnsubscribeRequestState();
          },
          onFailed: () => {
            executeOnErrorWithErrorCheck({
              error: unsubscribeProspectError,
              onError: () => {
                handleClearSelection();
                resetSequenceAndStepData();
                toaster.error(unsubscribeProspectError.message);
                resetUnsubscribeRequestState();
              },
            });
          },
        });
      },
    });
  }, [unsubscribeProspectStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateProspectFiltersViewRequestStatus,
      onSuccess: () => {
        toaster.success('Successfully saved as a view.');
      },
    });
  }, [updateProspectFiltersViewRequestStatus]);

  const hideShowProspectDetailsModal = useCallback(() => {
    setShowProspectDetailsModal(false);
    setSelectedContactId(null);

    // Dispatch event for child views to handle cleanup
    dispatchEvent('prospect:closeProspectDetailsModal');

    // Reset URL to base route, preserving query parameters
    closeProspectDetails();

    // Refresh data when closing modal from kanban view
    const isKanbanRoute = location.pathname.includes('/crm/kanban');
    if (isKanbanRoute) {
      fetchProspectListAndCount();
    }
  }, [
    closeProspectDetails,
    dispatchEvent,
    location.pathname,
    fetchProspectListAndCount,
  ]);

  const resetSelectedId = useCallback(() => {
    setSelectedContactId(null);
  }, []);

  const isKanbanRoute = location.pathname.includes('/crm/kanban');
  const containerClassName = [
    'prospect-container',
    'p-0',
    isKanbanRoute ? 'prospect-container--kanban' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Container fluid className={containerClassName}>
      <ErrorBoundaryWrapper>
        <Suspense>
          <ProspectHeader
            viewAddToSequenceModal={viewAddToSequenceModal}
            viewDeleteProspectModal={viewDeleteProspectModal}
            viewUnsubscribeProspectModal={viewUnsubscribeProspectModal}
            viewEmailVerificationModal={viewEmailVerificationModal}
            fetchProspectListAndCount={fetchProspectListAndCount}
            viewFiltersModal={viewFiltersModal}
            handleClearSelection={handleClearSelection}
          />
          <Switch>
            {/* List View Routes */}
            <Route exact path="/crm/list" component={ProspectListContent} />
            <Route
              exact
              path="/crm/list/:prospectId"
              component={ProspectListContent}
            />
            <Route
              exact
              path="/crm/list/:prospectId/:tab"
              component={ProspectListContent}
            />

            {/* Kanban View Routes */}
            <Route exact path="/crm/kanban" component={ProspectKanban} />
            <Route
              exact
              path="/crm/kanban/:prospectId"
              component={ProspectKanban}
            />
            <Route
              exact
              path="/crm/kanban/:prospectId/:tab"
              component={ProspectKanban}
            />
            <Redirect to="/crm/list" />
          </Switch>
          {/* ProspectDetails Modal - Shared between List and Kanban views */}
          {showProspectDetailsModal && selectedContactId && (
            <ProspectDetails
              show={showProspectDetailsModal}
              onHide={hideShowProspectDetailsModal}
              selectedProspectId={selectedContactId}
              setSelectedProspectId={setSelectedContactId}
              resetSelectedProspectId={resetSelectedId}
              selectedProspectData={singleProspectData}
              selectedProspectSequences={singleProspectSequenceDetails}
              getSingleProspectDetailsStatus={getSingleProspectDetailsStatus}
              getSingleProspectSequencesDetailsStatus={
                getSingleProspectSequencesDetailsStatus
              }
              isTagsFilterApplied={false}
              defaultTab={
                tabParam === 'activity' ||
                tabParam === 'sequences' ||
                tabParam === 'contacts' ||
                tabParam === 'notes' ||
                tabParam === 'tasks' ||
                tabParam === 'email'
                  ? (tabParam as any)
                  : 'activity'
              }
              {...prospectDetailsProps}
            />
          )}
          {/* ProspectFilters Modal - Shared between List and Kanban views */}
          {showProspectFilterModal && (
            <ProspectFiltersModal
              show={showProspectFilterModal}
              onClose={() => handleCloseProspectFilterModal()}
            />
          )}
          {showAddToSequenceReportModal && (
            <AddToSequenceReportModal
              show={showAddToSequenceReportModal}
              onHide={hideAddToSequenceReportModal}
              reportDataStructure={reportDataStructure}
              modalTitle={
                <div className="d-flex">
                  <div className="h-25">
                    <ImageIcon src={Images.ProspectActivityEmailSentBlue} />
                  </div>
                  <div className="pl-3">Add to Sequence</div>
                </div>
              }
              isAddSequenceStateModal={true}
            />
          )}
        </Suspense>
      </ErrorBoundaryWrapper>
    </Container>
  );
};

export default Prospect;
